import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { login, checkAuth } from '../../store/slices/authSlice';
import { tokenManager } from '../../services/api';
import { adminApiService } from '../../services/adminApi';

const AuthDebug = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, admin, loading, error } = useSelector(state => state.auth);
  const [credentials, setCredentials] = useState({
    username: 'admin',
    password: 'admin'
  });
  const [message, setMessage] = useState('');

  const handleInputChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleLogin = async () => {
    try {
      setMessage('🔄 Attempting login...');
      console.log('🔄 Attempting login with:', credentials);
      
      const result = await dispatch(login(credentials)).unwrap();
      console.log('✅ Login successful:', result);
      setMessage('✅ Login successful!');
    } catch (error) {
      console.error('❌ Login failed:', error);
      setMessage(`❌ Login failed: ${error}`);
    }
  };

  const checkTokenStatus = () => {
    const token = tokenManager.getToken();
    console.log('🔍 Current token:', token);
    console.log('🔍 Token exists:', !!token);
    console.log('🔍 IsAuthenticated:', isAuthenticated);
    console.log('🔍 Admin object:', admin);
    console.log('🔍 localStorage keys:', Object.keys(localStorage));
    console.log('🔍 ccbc_access_token:', localStorage.getItem('ccbc_access_token'));
    setMessage(`Token exists: ${!!token}, Authenticated: ${isAuthenticated}, Token length: ${token?.length || 0}`);
  };

  const setWorkingToken = async () => {
    try {
      // Set a fresh token (valid until 2025-08-31)
      const workingToken = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhZG1pbiIsImV4cCI6MTc1NjYyODY3NX0.hOZy5_VOTqvX5TKDUEPkk4QbUnWe31oKnyUJ4EBFHZ8';
      tokenManager.setToken(workingToken);
      setMessage('✅ Working token set! Updating auth state...');
      
      // Force update the authentication state
      const result = await dispatch(checkAuth()).unwrap();
      console.log('🔄 Auth state updated:', result);
      setMessage('✅ Token set and auth state updated! Navigation should work now.');
      
    } catch (error) {
      console.error('❌ Failed to update auth state:', error);
      setMessage('⚠️ Token set but auth state update failed. Try refreshing page manually.');
    }
  };

  const testApiCall = async () => {
    try {
      setMessage('🔄 Testing tithe receipts API call...');
      console.log('🔄 Testing API call to tithe receipts...');
      
      const token = tokenManager.getToken();
      console.log('🔍 Using token:', token);
      
      const response = await adminApiService.titheReceipts.getAll();
      console.log('✅ Tithe receipts API call successful:', response.data);
      setMessage('✅ Tithe receipts API call successful! Check console for details.');
    } catch (error) {
      console.error('❌ Tithe receipts API call failed:', error);
      setMessage(`❌ Tithe receipts API call failed: ${error.message || error} (Status: ${error.response?.status || 'Unknown'})`);
    }
  };

  const testEventsApi = async () => {
    try {
      setMessage('🔄 Testing events API call...');
      console.log('🔄 Testing API call to events...');
      
      const token = tokenManager.getToken();
      console.log('🔍 Using token for events:', token);
      
      const response = await adminApiService.events.getAll();
      console.log('✅ Events API call successful:', response.data);
      setMessage(`✅ Events API call successful! Found ${response.data?.length || 0} events. Check console for details.`);
    } catch (error) {
      console.error('❌ Events API call failed:', error);
      setMessage(`❌ Events API call failed: ${error.message || error} (Status: ${error.response?.status || 'Unknown'})`);
    }
  };

  const testAttendanceApi = async () => {
    try {
      setMessage('🔄 Testing attendance API call...');
      console.log('🔄 Testing API call to attendance...');
      
      const token = tokenManager.getToken();
      console.log('🔍 Using token for attendance:', token);
      
      // Test simple getAll first
      const response = await adminApiService.attendance.getAll({ limit: 10 });
      console.log('✅ Attendance API call successful:', response);
      console.log('✅ Attendance response.data:', response.data);
      console.log('✅ Attendance data array:', Array.isArray(response.data));
      setMessage(`✅ Attendance API call successful! Found ${response.data?.length || 0} records. Check console for details.`);
    } catch (error) {
      console.error('❌ Attendance API call failed:', error);
      setMessage(`❌ Attendance API call failed: ${error.message || error} (Status: ${error.response?.status || 'Unknown'})`);
    }
  };

  const clearToken = () => {
    tokenManager.clearTokens();
    setMessage('🗑️ Tokens cleared. Please refresh page.');
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-md mx-auto mt-8">
      <h2 className="text-xl font-bold mb-4">Authentication Debug</h2>
      
      <div className="mb-4">
        <p><strong>Status:</strong> {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}</p>
        <p><strong>Loading:</strong> {loading ? 'Yes' : 'No'}</p>
        <p><strong>Admin:</strong> {admin ? admin.username : 'None'}</p>
        {error && <p className="text-red-600"><strong>Error:</strong> {error}</p>}
        {message && <p className="text-blue-600"><strong>Message:</strong> {message}</p>}
      </div>

      <button
        onClick={checkTokenStatus}
        className="w-full mb-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
      >
        Check Token Status (See Console)
      </button>

      <button
        onClick={setWorkingToken}
        className="w-full mb-2 bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
      >
        Set Working Token & Update Auth
      </button>

      <button
        onClick={testApiCall}
        className="w-full mb-2 bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600"
      >
        Test Tithe Receipts API
      </button>

      <button
        onClick={testEventsApi}
        className="w-full mb-2 bg-indigo-500 text-white px-4 py-2 rounded hover:bg-indigo-600"
      >
        Test Events API
      </button>

      <button
        onClick={testAttendanceApi}
        className="w-full mb-2 bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600"
      >
        Test Attendance API
      </button>

      <button
        onClick={clearToken}
        className="w-full mb-2 bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
      >
        Clear Tokens
      </button>

      <div className="mt-4">
        <h3 className="text-lg font-semibold mb-2">Manual Login</h3>
        <input
          type="text"
          name="username"
          value={credentials.username}
          onChange={handleInputChange}
          placeholder="Username"
          className="w-full mb-2 p-2 border rounded"
        />
        <input
          type="password"
          name="password"
          value={credentials.password}
          onChange={handleInputChange}
          placeholder="Password"
          className="w-full mb-2 p-2 border rounded"
        />
        <button
          onClick={handleLogin}
          className="w-full bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
        >
          Login
        </button>
      </div>
    </div>
  );
};

export default AuthDebug;
